import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { message } from "antd";
import {
  FaArrowRotateRight,
  FaBuilding,
  FaChartLine,
  FaChartPie,
  FaDownload,
  FaListCheck,
  FaRegFileLines,
  FaRegFolderOpen,
  FaUserGroup,
  FaUsers,
} from "react-icons/fa6";

import { BiInfoCircle } from "react-icons/bi";

import Header from "../../components/Header";
import { analiseAcessos } from "../../services/http/dashboardService";
import {
  FiltrosAnalise,
  filtrosDaQuery,
  filtrosParaQuery,
  formatarData,
  formatarNumero,
  periodoValido,
} from "./FiltrosAnalise";
import "./styles.scss";

// Dashboard "Análise de Acessos" (RD 94908), conforme mockup anexo à RD.

const CORES = ["#1478ff", "#48c78e", "#8152d8", "#f5a623", "#9aa3ad"];

const percentual = (quantidade, total) => (total ? Math.round((quantidade * 1000) / total) / 10 : 0);

function Info({ texto }) {
  return (
    <span className="info" title={texto} aria-label={texto}>
      i
    </span>
  );
}

function Kpi({ icone, titulo, indicador, comparacao }) {
  const variacao = indicador?.variacao;
  let classe = "neutral";
  let texto = "sem base anterior";

  if (variacao !== null && variacao !== undefined) {
    classe = variacao > 0 ? "positive" : variacao < 0 ? "negative" : "neutral";
    texto = `${variacao > 0 ? "↑ " : variacao < 0 ? "↓ " : ""}${formatarNumero(Math.abs(variacao))}%`;
  }

  return (
    <div className="kpi-card">
      <div className="kpi-icon" aria-hidden="true">
        {icone}
      </div>
      <div className="kpi-content">
        <span>{titulo}</span>
        <strong>{formatarNumero(indicador?.atual)}</strong>
        <small className={classe}>
          {texto} <em>vs {comparacao}</em>
        </small>
      </div>
    </div>
  );
}

function Ranking({ icone, titulo, info, coluna, unidade, itens = [], tipo, query, barra }) {
  const maior = itens[0]?.quantidade || 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2>
          <span className="blue-icon">{icone}</span>
          {titulo}
        </h2>
        <Info texto={info} />
      </div>

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>{coluna}</th>
            <th>{unidade}</th>
          </tr>
        </thead>
        <tbody>
          {itens.length > 0 ? (
            itens.map((item) => (
              <tr key={item.posicao}>
                <td>{item.posicao}</td>
                <td title={item.caminho || item.nome}>
                  {barra ? (
                    <div className="ranking-name">
                      <span>{item.nome}</span>
                      <div className="progress">
                        <span style={{ width: `${maior ? (item.quantidade * 100) / maior : 0}%` }} />
                      </div>
                    </div>
                  ) : (
                    item.nome
                  )}
                </td>
                <td>{formatarNumero(item.quantidade)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="vazio">
                Sem dados no período
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Link to={`/dashboard/relatorio/${tipo}?${query}`} className="report-link">
        Ver relatório completo →
      </Link>
    </div>
  );
}

function Rosca({ itens, classe, rotulo }) {
  const total = itens.reduce((soma, item) => soma + item.quantidade, 0);
  let acumulado = 0;
  const fatias = itens.map((item, indice) => {
    const inicio = acumulado;
    acumulado += total ? (item.quantidade * 360) / total : 0;
    return `${CORES[indice % CORES.length]} ${inicio}deg ${acumulado}deg`;
  });

  return (
    <>
      <div
        className={classe}
        role="img"
        aria-label={`${rotulo}: total ${formatarNumero(total)}`}
        style={{ background: total ? `conic-gradient(${fatias.join(", ")})` : "#edf0f3" }}
      >
        <div className="donut-center">
          <strong>{formatarNumero(total)}</strong>
          <span>Total</span>
        </div>
      </div>

      <div className="legend">
        {itens.map((item, indice) => (
          <div key={item.nome}>
            <span className="legend-color" style={{ background: CORES[indice % CORES.length] }} />
            <strong>{item.nome}</strong>
            <small>
              {formatarNumero(percentual(item.quantidade, total))}% ({formatarNumero(item.quantidade)})
            </small>
          </div>
        ))}
      </div>
    </>
  );
}

/** Passo "redondo" do eixo Y para 4 divisões (1, 2, 5 x 10^n). */
const passoEixo = (maximo) => {
  if (maximo <= 4) return 1;
  const bruto = maximo / 4;
  const magnitude = 10 ** Math.floor(Math.log10(bruto));
  const normalizado = bruto / magnitude;
  return (normalizado <= 1 ? 1 : normalizado <= 2 ? 2 : normalizado <= 5 ? 5 : 10) * magnitude;
};

const formatarEixo = (valor) =>
  valor >= 1000 ? `${formatarNumero(valor / 1000)}k` : formatarNumero(valor);

function Evolucao({ serie = [] }) {
  const LARGURA = 700;
  const ALTURA = 220;
  const passo = passoEixo(Math.max(0, ...serie.map((dia) => Math.max(dia.acessos, dia.downloads))));
  const topo = passo * 4;

  const pontos = (campo) =>
    serie
      .map((dia, indice) => {
        const x = serie.length > 1 ? (indice * LARGURA) / (serie.length - 1) : LARGURA / 2;
        const y = ALTURA - (dia[campo] / topo) * ALTURA;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");

  const quantidadeRotulos = Math.min(7, serie.length);
  const rotulosX = Array.from({ length: quantidadeRotulos }, (_, k) =>
    serie[quantidadeRotulos > 1 ? Math.round((k * (serie.length - 1)) / (quantidadeRotulos - 1)) : 0]
  );

  return (
    <div className="line-chart">
      <div className="y-axis" aria-hidden="true">
        {[4, 3, 2, 1, 0].map((k) => (
          <span key={k}>{formatarEixo(passo * k)}</span>
        ))}
      </div>

      <div className="chart-area">
        <svg
          viewBox={`0 0 ${LARGURA} ${ALTURA}`}
          preserveAspectRatio="none"
          className="svg-chart"
          role="img"
          aria-label="Evolução diária de acessos e downloads no período"
        >
          {[0, 1, 2, 3].map((k) => (
            <line key={k} x1="0" x2={LARGURA} y1={(k * ALTURA) / 4} y2={(k * ALTURA) / 4} className="grid-line" />
          ))}
          <polyline points={pontos("acessos")} className="line-access" />
          <polyline points={pontos("downloads")} className="line-download" />
          {serie.length === 1 && (
            <>
              <circle cx={LARGURA / 2} cy={ALTURA - (serie[0].acessos / topo) * ALTURA} r="5" className="ponto-access" />
              <circle cx={LARGURA / 2} cy={ALTURA - (serie[0].downloads / topo) * ALTURA} r="5" className="ponto-download" />
            </>
          )}
        </svg>

        <div className="x-axis">
          {rotulosX.map((dia, indice) => (
            <span key={indice}>{formatarData(dia?.data).substring(0, 5)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

const Dashboard = () => {
  const location = useLocation();
  // Filtros podem vir da query ("Voltar ao dashboard" dos relatórios); senão, padrão.
  const [filtros, setFiltros] = useState(() => filtrosDaQuery(location.search));
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtros);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState(null);

  const carregar = async (consulta = filtros) => {
    if (!periodoValido(consulta)) {
      message.warning("Informe um período válido: a data final deve ser maior ou igual à inicial.");
      return;
    }

    setCarregando(true);
    const resposta = await analiseAcessos(consulta);
    setCarregando(false);

    if (resposta.fail) {
      message.error(resposta.errors?.message || "Não foi possível carregar a análise de acessos");
      return;
    }

    setDados(resposta.data);
    setFiltrosAplicados(consulta);
    setAtualizadoEm(new Date());
  };

  useEffect(() => {
    carregar();
  }, []);

  const query = filtrosParaQuery(filtrosAplicados);
  const comparacao = dados
    ? `${formatarData(dados.dataInicioComparacao)} – ${formatarData(dados.dataFimComparacao)}`
    : "";

  return (
    <div>
      <Header style={{ marginBottom: "2rem" }} mostrarPesquisar={false} />

      <main className="analise-acessos">
        <section className="page-header">
          <h1>Análise de Acessos</h1>
          <p>
            Acompanhe o comportamento de acesso e download de pastas e arquivos no Geely Mídia
            Center.
          </p>
        </section>

        <FiltrosAnalise
          filtros={filtros}
          setFiltros={setFiltros}
          onAtualizar={() => carregar()}
          carregando={carregando}
        />

        {!dados ? (
          <div className="carregando">{carregando ? "Carregando..." : "Sem dados para exibir."}</div>
        ) : (
          <>
            <section className="kpi-grid">
              <Kpi icone={<FaRegFolderOpen />} titulo="Pastas acessadas" indicador={dados.pastasAcessadas} comparacao={comparacao} />
              <Kpi icone={<FaRegFileLines />} titulo="Arquivos acessados" indicador={dados.arquivosAcessados} comparacao={comparacao} />
              <Kpi icone={<FaDownload />} titulo="Downloads realizados" indicador={dados.downloads} comparacao={comparacao} />
              <Kpi icone={<FaUsers />} titulo="Usuários ativos" indicador={dados.usuariosAtivos} comparacao={comparacao} />
              <Kpi icone={<FaBuilding />} titulo="Dealers ativos" indicador={dados.dealersAtivos} comparacao={comparacao} />
            </section>

            <section className="ranking-grid">
              <Ranking
                icone={<FaRegFolderOpen />}
                titulo="Pastas mais acessadas"
                info="Pastas abertas na galeria (inclui subpastas quando uma pasta é filtrada)."
                coluna="Pasta"
                unidade="Acessos"
                itens={dados.pastasMaisAcessadas}
                tipo="pastas"
                query={query}
                barra
              />
              <Ranking
                icone={<FaRegFileLines />}
                titulo="Arquivos mais acessados (geral)"
                info="Arquivos visualizados no período."
                coluna="Arquivo"
                unidade="Acessos"
                itens={dados.arquivosMaisAcessados}
                tipo="arquivos"
                query={query}
              />
              <Ranking
                icone={<FaBuilding />}
                titulo="Dealers que mais baixam"
                info="Downloads de arquivos e pastas por concessionária do usuário. Links públicos não entram."
                coluna="Dealer"
                unidade="Downloads"
                itens={dados.dealersMaisBaixam}
                tipo="dealers"
                query={query}
              />

              <div className="card profile-card">
                <div className="card-header">
                  <h2>
                    <span className="blue-icon">
                      <FaUserGroup />
                    </span>
                    Acessos por perfil
                  </h2>
                  <Info texto="Arquivos visualizados, por perfil do usuário." />
                </div>
                <div className="donut-wrapper">
                  <Rosca itens={dados.acessosPorPerfil} classe="donut-chart" rotulo="Acessos por perfil" />
                </div>
                <Link to={`/dashboard/relatorio/perfis?${query}`} className="report-link">
                  Ver relatório completo →
                </Link>
              </div>
            </section>

            <section className="bottom-grid">
              <div className="card trend-card">
                <div className="card-header">
                  <h2>
                    <span className="blue-icon">
                      <FaChartLine />
                    </span>
                    Evolução de acessos e downloads
                  </h2>
                  <Info texto="Acessos = arquivos visualizados; Downloads = arquivos e pastas baixados, por dia." />
                </div>
                <div className="chart-legend">
                  <span>
                    <i className="dot blue" />
                    Acessos
                  </span>
                  <span>
                    <i className="dot green" />
                    Downloads
                  </span>
                </div>
                <Evolucao serie={dados.evolucao} />
              </div>

              <div className="card action-card">
                <div className="card-header">
                  <h2>
                    <span className="blue-icon">
                      <FaChartPie />
                    </span>
                    Tipos de ação
                  </h2>
                  <Info texto="Visualizações de arquivos e downloads realizados no período." />
                </div>
                <div className="action-content">
                  <Rosca
                    itens={[
                      { nome: "Visualizações", quantidade: dados.totalAcessos },
                      { nome: "Downloads", quantidade: dados.totalDownloads },
                    ]}
                    classe="donut-action"
                    rotulo="Tipos de ação"
                  />
                </div>
              </div>

              <div className="card summary-card">
                <div className="card-header">
                  <h2>Resumo do período</h2>
                  <Info texto="Médias diárias = total dividido pelo número de dias do período." />
                </div>
                <div className="summary-item">
                  <span className="summary-icon">
                    <FaRegFileLines />
                  </span>
                  <span>Total de acessos</span>
                  <strong>{formatarNumero(dados.totalAcessos)}</strong>
                </div>
                <div className="summary-item">
                  <span className="summary-icon green">
                    <FaDownload />
                  </span>
                  <span>Total de downloads</span>
                  <strong>{formatarNumero(dados.totalDownloads)}</strong>
                </div>
                <div className="summary-item">
                  <span className="summary-icon green">
                    <FaListCheck />
                  </span>
                  <span>Média diária de acessos</span>
                  <strong>{formatarNumero(dados.mediaDiariaAcessos)}</strong>
                </div>
                <div className="summary-item">
                  <span className="summary-icon green">
                    <FaDownload />
                  </span>
                  <span>Média diária de downloads</span>
                  <strong>{formatarNumero(dados.mediaDiariaDownloads)}</strong>
                </div>
              </div>
            </section>

            <footer className="dashboard-footer">
              <span>
                <BiInfoCircle /> Os dados apresentados são referentes ao período selecionado (
                {formatarData(dados.dataInicio)} – {formatarData(dados.dataFim)}).
              </span>
              <span>
                Última atualização:{" "}
                {atualizadoEm?.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                <FaArrowRotateRight />
              </span>
            </footer>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
